import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATES = [
  { code: "NSW", label: "New South Wales", curriculum: "NESA syllabus" },
  { code: "VIC", label: "Victoria", curriculum: "VCAA curriculum" },
  { code: "QLD", label: "Queensland", curriculum: "QCAA curriculum" },
  { code: "WA", label: "Western Australia", curriculum: "SCSA curriculum" },
  { code: "SA", label: "South Australia", curriculum: "SACE" },
  { code: "NT", label: "Northern Territory", curriculum: "SACE (NT)" },
  { code: "TAS", label: "Tasmania", curriculum: "TASC" },
  { code: "ACT", label: "ACT", curriculum: "BSSS curriculum" },
];

const SUBJECTS = ["English", "Maths", "Science", "History", "Geography", "PE / Health", "Art", "Drama", "Music",
  "Languages", "Design & Tech", "Digital Tech", "Commerce / Business", "Food Tech", "Religion"];

const CHALLENGES = ["Starting tasks", "Staying focused", "Organisation", "Time blindness", "Cramming / last-minute panic",
  "Motivation / boredom", "Clashes with teachers", "Forgetting homework"];

const VOICE_OPTIONS = [
  { id: "nova", label: "Nova", desc: "Warm & bright (default)" },
  { id: "shimmer", label: "Shimmer", desc: "Soft & calm" },
  { id: "alloy", label: "Alloy", desc: "Clear & neutral" },
];

const inputCls = "w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base";
const chip = (on) => `px-3 py-1.5 rounded-full border-2 border-black text-sm font-bold transition-all ${on ? "bg-[#FFE066] shadow-[2px_2px_0px_rgba(0,0,0,1)]" : "bg-white text-gray-500"}`;

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ child_name: "", age: "", year_level: 10, state: "", subjects: [], challenges: [], adhd_notes: "", voice: "nova", phone: "" });
  const [customSubject, setCustomSubject] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const toggle = (key, val) =>
    setF((p) => ({ ...p, [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val] }));

  const canNext = [
    f.child_name.trim().length > 0,
    !!f.state,
    f.subjects.length > 0,
    true,
  ][step];

  const finish = async () => {
    setBusy(true);
    try {
      const adhd_profile = [f.challenges.length ? `Main challenges: ${f.challenges.join(", ")}.` : "", f.adhd_notes.trim()]
        .filter(Boolean).join(" ");
      await api.post("/onboarding", {
        child_name: f.child_name.trim(), age: f.age ? Number(f.age) : null, year_level: f.year_level,
        state: f.state, subjects: f.subjects, adhd_profile, voice: f.voice, phone: f.phone.trim(),
      });
      await refresh();
      toast.success(`All set! Mia is ready for ${f.child_name.trim()}.`);
      navigate("/");
    } catch {
      toast.error("Couldn't save that — try again.");
    }
    setBusy(false);
  };

  const steps = [
    <div key="s0" className="space-y-4">
      <h2 className="font-heading text-2xl font-semibold">Who's Mia coaching?</h2>
      <input className={inputCls} placeholder="Your child's first name" value={f.child_name}
        onChange={(e) => setF({ ...f, child_name: e.target.value })} data-testid="onboarding-child-name" />
      <input className={inputCls} type="number" placeholder="Age (optional)" value={f.age}
        onChange={(e) => setF({ ...f, age: e.target.value })} data-testid="onboarding-age" />
      <div>
        <p className="font-bold text-sm mb-2">Year level</p>
        <div className="flex flex-wrap gap-2">
          {[7, 8, 9, 10, 11, 12].map((y) => (
            <button key={y} onClick={() => setF({ ...f, year_level: y })} data-testid={`onboarding-year-${y}`}
              className={chip(f.year_level === y)}>Year {y}</button>
          ))}
        </div>
      </div>
    </div>,
    <div key="s1" className="space-y-4">
      <h2 className="font-heading text-2xl font-semibold">Which state?</h2>
      <p className="text-sm text-gray-600">Mia aligns study advice with your state's curriculum and ATAR standards.</p>
      <div className="grid grid-cols-2 gap-2">
        {STATES.map((s) => (
          <button key={s.code} onClick={() => setF({ ...f, state: s.code })} data-testid={`onboarding-state-${s.code}`}
            className={`p-3 rounded-xl border-2 border-black text-left transition-all ${f.state === s.code ? "bg-[#FFE066] shadow-[3px_3px_0px_rgba(0,0,0,1)]" : "bg-white"}`}>
            <p className="font-bold text-sm">{s.label}</p>
            <p className="text-xs text-gray-600">{s.curriculum}</p>
          </button>
        ))}
      </div>
    </div>,
    <div key="s2" className="space-y-4">
      <h2 className="font-heading text-2xl font-semibold">Subjects this year</h2>
      <div className="flex flex-wrap gap-2">
        {[...SUBJECTS, ...f.subjects.filter((s) => !SUBJECTS.includes(s))].map((s) => (
          <button key={s} onClick={() => toggle("subjects", s)} data-testid={`onboarding-subject-${s.replace(/[^a-zA-Z]/g, "")}`}
            className={chip(f.subjects.includes(s))}>{s}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <input className={inputCls} placeholder="Add another subject" value={customSubject}
          onChange={(e) => setCustomSubject(e.target.value)} data-testid="onboarding-custom-subject" />
        <button
          onClick={() => { if (customSubject.trim()) { toggle("subjects", customSubject.trim()); setCustomSubject(""); } }}
          data-testid="onboarding-add-subject"
          className="shrink-0 px-4 bg-[#51CF66] border-2 border-black rounded-xl font-bold shadow-[2px_2px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all">
          Add
        </button>
      </div>
    </div>,
    <div key="s3" className="space-y-4">
      <h2 className="font-heading text-2xl font-semibold">ADHD profile & Mia's voice</h2>
      <p className="text-sm text-gray-600">This shapes how Mia coaches — pick what fits.</p>
      <div className="flex flex-wrap gap-2">
        {CHALLENGES.map((c) => (
          <button key={c} onClick={() => toggle("challenges", c)} data-testid={`onboarding-challenge-${c.replace(/[^a-zA-Z]/g, "")}`}
            className={chip(f.challenges.includes(c))}>{c}</button>
        ))}
      </div>
      <textarea className={`${inputCls} min-h-[80px]`} placeholder="Anything else Mia should know? (diagnosis, meds timing, what motivates them...)"
        value={f.adhd_notes} onChange={(e) => setF({ ...f, adhd_notes: e.target.value })} data-testid="onboarding-adhd-notes" />
      <div>
        <p className="font-bold text-sm mb-1">Teen's mobile <span className="font-normal text-gray-500">(optional — Mia texts urgency nudges)</span></p>
        <input className={inputCls} type="tel" placeholder="04XX XXX XXX"
          value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="onboarding-phone" />
      </div>
      <div>
        <p className="font-bold text-sm mb-2">Mia's voice</p>
        <div className="grid grid-cols-3 gap-2">
          {VOICE_OPTIONS.map((v) => (
            <button key={v.id} onClick={() => setF({ ...f, voice: v.id })} data-testid={`onboarding-voice-${v.id}`}
              className={`p-3 rounded-xl border-2 border-black text-left transition-all ${f.voice === v.id ? "bg-[#F2C6DE] shadow-[3px_3px_0px_rgba(0,0,0,1)]" : "bg-white"}`}>
              <p className="font-bold text-sm">{v.label}</p>
              <p className="text-[11px] text-gray-600">{v.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>,
  ];

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-16 space-y-6">
      <div className="flex items-center gap-3">
        <img src="/mascot.png" alt="mascot" className="w-14 h-14 rounded-full border-4 border-black shadow-[3px_3px_0px_rgba(0,0,0,1)] bg-white object-cover" />
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-1.5"><Sparkles size={20} className="text-[#FFB800]" /> Set up StudySpark</h1>
          <p className="text-xs text-gray-600">Step {step + 1} of 4</p>
        </div>
      </div>

      <div className="flex gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-2 flex-1 rounded-full border-2 border-black ${i <= step ? "bg-[#4D96FF]" : "bg-white"}`} />
        ))}
      </div>

      <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6" data-testid={`onboarding-step-${step}`}>
        {steps[step]}
      </div>

      <div className="flex gap-3">
        {step > 0 && (
          <button onClick={() => setStep(step - 1)} data-testid="onboarding-back-button"
            className="flex items-center gap-1 px-5 py-3 bg-white border-2 border-black rounded-full font-bold shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all">
            <ChevronLeft size={18} /> Back
          </button>
        )}
        {step < 3 ? (
          <button onClick={() => canNext && setStep(step + 1)} disabled={!canNext} data-testid="onboarding-next-button"
            className="flex-1 flex items-center justify-center gap-1 px-5 py-3 bg-[#4D96FF] border-2 border-black rounded-full font-bold shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50">
            Next <ChevronRight size={18} />
          </button>
        ) : (
          <button onClick={finish} disabled={busy} data-testid="onboarding-finish-button"
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#51CF66] border-2 border-black rounded-full font-bold shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all disabled:opacity-60">
            {busy && <Loader2 size={18} className="animate-spin" />} Meet Mia
          </button>
        )}
      </div>
    </div>
  );
}
